import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Shield, CheckCircle, ArrowRight, FileText } from 'lucide-react';
import OnboardingCard, { OnboardingButton, OnboardingInput, OnboardingHeading } from '../components/OnboardingCard';

export function parseProfileNames(user, onboardingState) {
  const firstName = onboardingState?.first_name || user?.first_name || '';
  const lastName = onboardingState?.last_name || user?.last_name || '';
  if (firstName?.trim() && lastName?.trim()) {
    return { firstName: firstName.trim(), lastName: lastName.trim() };
  }
  const fullName = user?.full_name || user?.name || '';
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
  }
  if (parts.length === 1) {
    return { firstName: parts[0], lastName: '' };
  }
  return { firstName: firstName.trim(), lastName: lastName.trim() };
}

export function getMissingInfoRequirements(user, onboardingState, formData) {
  const { firstName, lastName } = parseProfileNames(user, onboardingState);
  const termsAccepted = Boolean(
    formData?.termsAccepted || onboardingState?.terms_accepted
  );

  return {
    needsFirstName: !firstName?.trim(),
    needsLastName: !lastName?.trim(),
    needsTerms: !termsAccepted,
    firstName,
    lastName,
    termsAccepted,
  };
}

export function isMissingInfoComplete(user, onboardingState, formData) {
  const req = getMissingInfoRequirements(user, onboardingState, formData);
  return !req.needsFirstName && !req.needsLastName && !req.needsTerms;
}

const MissingInfoStep = ({ formData, updateFormData, nextStep, user, onboardingState }) => {
  const requirements = useMemo(
    () => getMissingInfoRequirements(user, onboardingState, formData),
    [user, onboardingState, formData]
  );
  const [errors, setErrors] = useState({});

  const termsOnly = requirements.needsTerms && !requirements.needsFirstName && !requirements.needsLastName;

  const validateForm = () => {
    const newErrors = {};

    if (requirements.needsFirstName && !formData.firstName?.trim()) {
      newErrors.firstName = 'First name is required';
    }
    if (requirements.needsLastName && !formData.lastName?.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    if (requirements.needsTerms && !formData.termsAccepted) {
      newErrors.terms = 'Please accept the terms to continue';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleContinue = () => {
    if (!validateForm()) return;

    const { firstName, lastName } = parseProfileNames(user, onboardingState);
    nextStep({
      first_name: formData.firstName?.trim() || firstName,
      last_name: formData.lastName?.trim() || lastName,
      email: user?.email || onboardingState?.email,
      terms_accepted: true,
      basic_info_completed: true,
    });
  };

  const showNameFields = requirements.needsFirstName || requirements.needsLastName;

  return (
    <OnboardingCard showGlow={true} glowIntensity="normal">
      <div className="p-6 sm:p-10">
        <div className="flex justify-center mb-6">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.1, duration: 0.4 }}
            className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center shadow-lg shadow-emerald-500/30"
          >
            {termsOnly ? (
              <FileText size={32} className="text-white" />
            ) : (
              <Shield size={32} className="text-white" />
            )}
          </motion.div>
        </div>

        <OnboardingHeading
          title={termsOnly ? 'Review and continue' : 'Almost ready'}
          subtitle={
            termsOnly
              ? 'Accept the terms below to continue to your goals.'
              : 'Confirm the remaining details before your credit report import.'
          }
        />

        {showNameFields && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.4 }}
            className="space-y-4 mt-6"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {requirements.needsFirstName && (
                <OnboardingInput
                  label="First Name"
                  placeholder="John"
                  value={formData.firstName}
                  onChange={(e) => updateFormData({ firstName: e.target.value })}
                  error={errors.firstName}
                  required
                  data-testid="onboarding-firstname-input"
                />
              )}
              {requirements.needsLastName && (
                <OnboardingInput
                  label="Last Name"
                  placeholder="Smith"
                  value={formData.lastName}
                  onChange={(e) => updateFormData({ lastName: e.target.value })}
                  error={errors.lastName}
                  required
                  data-testid="onboarding-lastname-input"
                />
              )}
            </div>
          </motion.div>
        )}

        {requirements.needsTerms && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.4 }}
            className={showNameFields ? 'mt-6' : 'mt-6'}
          >
            <label className="flex items-start gap-3 cursor-pointer group">
              <div className="relative flex-shrink-0 mt-0.5">
                <input
                  type="checkbox"
                  checked={formData.termsAccepted}
                  onChange={(e) => updateFormData({ termsAccepted: e.target.checked })}
                  className="sr-only"
                  data-testid="onboarding-terms-checkbox"
                />
                <div
                  className={`
                    w-5 h-5 rounded border-2 transition-all duration-200
                    ${formData.termsAccepted
                      ? 'bg-emerald-500 border-emerald-500'
                      : errors.terms
                        ? 'border-red-500'
                        : 'border-slate-500 group-hover:border-emerald-400'
                    }
                  `}
                >
                  {formData.termsAccepted && (
                    <motion.svg
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="w-full h-full text-white p-0.5"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </motion.svg>
                  )}
                </div>
              </div>
              <span className="text-sm text-slate-400 leading-relaxed">
                I agree to the{' '}
                <a href="/terms" className="text-emerald-400 hover:text-emerald-300 underline" target="_blank" rel="noopener noreferrer">
                  Terms of Service
                </a>{' '}
                and{' '}
                <a href="/privacy" className="text-emerald-400 hover:text-emerald-300 underline" target="_blank" rel="noopener noreferrer">
                  Privacy Policy
                </a>
              </span>
            </label>
            {errors.terms && (
              <p className="text-sm text-red-400 mt-1 ml-8">{errors.terms}</p>
            )}
          </motion.div>
        )}

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.4 }}
          className="mt-6 flex flex-wrap justify-center gap-4"
        >
          {[
            { icon: Shield, text: 'Bank-level encryption' },
            { icon: CheckCircle, text: 'No credit impact' },
          ].map((item, index) => (
            <div key={index} className="flex items-center gap-2 text-slate-400 text-sm">
              <item.icon size={14} className="text-emerald-500/70" />
              <span>{item.text}</span>
            </div>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.4 }}
          className="mt-8"
        >
          <OnboardingButton
            onClick={handleContinue}
            className="w-full"
            data-testid="onboarding-continue-btn"
          >
            Continue
            <ArrowRight size={20} />
          </OnboardingButton>
        </motion.div>
      </div>
    </OnboardingCard>
  );
};

export default MissingInfoStep;
